import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';

type Stage = 'loading' | 'ready' | 'no-session' | 'preexisting' | 'done';

// Tipos de enlace de GoTrue que legitiman fijar contraseña por primera vez:
// invitación (director/monitor nuevo), recuperación, confirmación de alta y
// magic link. Todos entregan un `access_token` fresco en el hash del enlace.
const FRESH_LINK_TYPES = new Set(['invite', 'recovery', 'signup', 'magiclink']);

// GUARD ANTI-SECUESTRO.
//
// `updateUser({ password })` cambia la contraseña de la sesión ACTIVA sea
// cual sea. El riesgo: que la sesión activa sea una cuenta PREEXISTENTE
// (p.ej. un admin ya logueado en el navegador) en vez de la del invitado
// recién creado → cualquiera que rellene el form le sobreescribiría la
// contraseña a esa cuenta. Blindamos aceptando SOLO sesiones que provienen
// de un enlace fresco de invitación/recuperación, no de una sesión
// PERSISTIDA (`INITIAL_SESSION`).
//
// Señal usada (y por qué NO otras): descartamos `invited_at` y
// `last_sign_in_at` del user object porque no son fiables aquí —
// create-monitor/handler.ts documenta que GoTrue no puebla `invited_at` a
// tiempo (rompía el invite con un 500, mig 095), y `last_sign_in_at` se
// setea al ACEPTAR la invitación (no es null para un invitado nuevo). La
// señal fiable es el ORIGEN de la sesión:
//   1) hash del enlace con `access_token` y `type` ∈ FRESH_LINK_TYPES, y/o
//   2) el evento de `onAuthStateChange` fue SIGNED_IN / PASSWORD_RECOVERY
//      (fresco), NO INITIAL_SESSION (sesión persistida = preexistente).
// Aceptamos si CUALQUIERA de las dos indica frescura (belt-and-suspenders):
// así un invitado legítimo nunca se bloquea aunque una de las dos señales
// no llegue (p.ej. supabase pliega el invite en INITIAL_SESSION pero el
// hash sigue presente). Solo bloqueamos cuando NINGUNA lo indica.
export function isFreshInviteOrigin(input: {
  event: AuthChangeEvent | null;
  hashHasToken: boolean;
  hashType: string | null;
}): boolean {
  const freshLink = input.hashHasToken && !!input.hashType && FRESH_LINK_TYPES.has(input.hashType);
  const freshEvent = input.event === 'SIGNED_IN' || input.event === 'PASSWORD_RECOVERY';
  return freshLink || freshEvent;
}

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Marca que la sesión activa proviene de un enlace fresco de invitación
  // (no de una cuenta preexistente). Se re-verifica antes de updateUser.
  const [originFresh, setOriginFresh] = useState(false);

  // Captura SÍNCRONA del hash del enlace en el primer render, ANTES de que
  // supabase-js lo consuma/limpie al detectar la sesión. Es la señal de que
  // la sesión llegó por un enlace de invitación/recuperación fresco.
  const [linkHash] = useState(() => {
    const raw = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
    const params = new URLSearchParams(raw);
    return { hasToken: params.has('access_token'), type: params.get('type') };
  });

  // Tras clicar el enlace de invitación, Supabase JS detecta el hash con
  // `access_token` y monta sesión vía `onAuthStateChange`. Esperamos a que
  // la sesión exista antes de mostrar el form. Si tras un timeout corto
  // no hay sesión, asumimos enlace inválido/caducado y pedimos reintentar.
  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (!data.session) setStage('no-session');
    }, 4000);

    // Acepta la sesión SOLO si proviene de un enlace fresco (guard
    // anti-secuestro). Si es una sesión persistida de cuenta preexistente,
    // no montamos el form: enviamos a la pantalla de "cuenta ya activa".
    function accept(session: Session | null, event: AuthChangeEvent | null) {
      if (cancelled || !session) return;
      clearTimeout(timeout);
      const fresh = isFreshInviteOrigin({
        event,
        hashHasToken: linkHash.hasToken,
        hashType: linkHash.type,
      });
      setEmail(session.user.email ?? null);
      setOriginFresh(fresh);
      setStage(fresh ? 'ready' : 'preexisting');
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) accept(session, event);
    });

    (async () => {
      const { data } = await supabase.auth.getSession();
      // Sin evento asociado: nos apoyamos solo en el hash del enlace.
      if (data.session) accept(data.session, null);
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [linkHash]);

  // Mensaje del navigator lock que supabase-js a veces lanza cuando dos
  // pestañas/contextos compiten por el storage del token. Es transitorio:
  // si `getSession()` devuelve sesión válida después, la operación fue
  // exitosa. Lo filtramos por substring porque la cadena exacta cambia
  // entre versiones del SDK ("Lock 'lock:sb-…-auth-token' was released
  // because another request stole it" / "released" / "navigator lock…").
  function isTransientLockError(message: string): boolean {
    return /lock|released/i.test(message);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    // Guard anti-secuestro (re-verificación antes de updateUser): si la
    // sesión activa no proviene de un enlace fresco de invitación, NO
    // tocamos la contraseña — sería una cuenta preexistente.
    if (!originFresh) {
      setStage('preexisting');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) {
        // Antes de mostrarle al usuario un fallo, comprobamos si era el
        // warning del navigator lock — en ese caso la sesión sigue viva
        // y `updateUser` casi siempre completó. Pedimos sesión fresca y,
        // si está, seguimos adelante.
        if (isTransientLockError(updErr.message)) {
          const { data: probeSess } = await supabase.auth.getSession();
          if (!probeSess.session) {
            throw updErr;
          }
          // sesión vigente → asumimos éxito (el password se persistió)
        } else {
          throw updErr;
        }
      }
      setStage('done');

      // Post-set-password (§3c): no llevamos al usuario directo al
      // dashboard; pasamos por /welcome (greeter + next steps + CTA).
      // Ese page decide a qué dashboard mandar según role.
      setTimeout(() => navigate('/welcome', { replace: true }), 600);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la contraseña.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 rounded-full bg-gold-primary/5 blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 rounded-full bg-gold-primary/3 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="VERTEX Performance" className="h-24 mx-auto mb-4" />
          <p className="text-text-secondary text-sm mt-1 font-inter">Define tu contraseña</p>
        </div>

        <div className="card shadow-gold">
          {stage === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
            </div>
          )}

          {stage === 'no-session' && (
            <div
              className="flex items-start gap-3 bg-error/10 border border-error/30 rounded-lg p-4"
              data-testid="set-password-no-session"
            >
              <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-error font-medium text-sm">Enlace no válido o caducado</p>
                <p className="text-text-secondary text-xs mt-2">
                  Pide al administrador que vuelva a enviarte la invitación. Los enlaces de
                  invitación caducan tras un tiempo por seguridad.
                </p>
              </div>
            </div>
          )}

          {stage === 'preexisting' && (
            <div
              className="flex flex-col gap-3 bg-error/10 border border-error/30 rounded-lg p-4"
              data-testid="set-password-preexisting"
            >
              <div className="flex items-start gap-3">
                <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-error font-medium text-sm">Esta cuenta ya está activa</p>
                  <p className="text-text-secondary text-xs mt-2">
                    Este enlace es solo para definir la contraseña de una cuenta nueva. Inicia
                    sesión con tu contraseña; si la olvidaste, usa «¿Olvidaste tu contraseña?» en la
                    pantalla de acceso.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="btn-gold w-full"
                data-testid="set-password-goto-login"
              >
                Ir al inicio de sesión
              </button>
            </div>
          )}

          {stage === 'done' && (
            <div
              className="flex items-start gap-3 bg-success/10 border border-success/30 rounded-lg p-4"
              data-testid="set-password-done"
            >
              <CheckCircle size={18} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-success font-medium text-sm">Contraseña guardada</p>
                <p className="text-text-secondary text-xs mt-2">Entrando al portal…</p>
              </div>
            </div>
          )}

          {stage === 'ready' && (
            <>
              <h2 className="font-oswald text-xl uppercase tracking-wider text-white mb-2">
                Bienvenido{email ? ',' : ''}
              </h2>
              {email && (
                <p className="text-text-secondary text-sm mb-6 font-mono break-all">{email}</p>
              )}
              {/* Esta página la ven TRES públicos distintos, no solo el
                  entrenador para el que se escribió: monitor invitado, director
                  reinvitado y —desde F5-B (mig 202)— el HIJO al que su tutor le
                  acaba de activar el acceso. Un chaval de 14 que lee "portal de
                  entrenadores" cree que se ha equivocado de enlace y no sigue. */}
              <p className="text-text-secondary text-sm mb-6">
                Define una contraseña para tu cuenta de VERTEX Performance.
              </p>

              {error && (
                <div className="flex items-center gap-2 bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
                  <AlertCircle size={16} className="text-error flex-shrink-0" />
                  <p className="text-error text-sm font-inter">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
                <div>
                  <label className="label">Contraseña</label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-9 pr-10"
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                      data-testid="set-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label">Repite la contraseña</label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-9"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                      data-testid="set-password-confirm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full mt-2 flex items-center justify-center gap-2"
                  data-testid="set-password-submit"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-bg-primary/30 border-t-bg-primary rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Establecer contraseña y entrar'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-text-secondary text-xs text-center mt-4 font-inter">
          Break your limits!!!
        </p>
      </div>
    </div>
  );
}
